// The rows of ECONOMY and WAR in the grammar of BUILD (mockup round 091
// card 09, R1892; ui-map-interface.md §12.1, §12.2, §12.5). Every list of
// the two menus draws the spec row of `buildrow.ts` or the fleet row of
// `fleetlist.ts`, under one kind of section head:
//
// - ECONOMY · PRODUCTION: one spec row per resource. The icon in its
//   resource ink, the rate per hour in the text ink and the hours to a full
//   store on line 2, the stock over its cap with a store bar in the figure
//   column.
// - ECONOMY · AUCTION: on BROWSE one spec row per print or isotope lot,
//   with BUY (or DELIST on an own listing) as the press; on ADD OFFER one
//   spec row per loose print, with its price field and LIST.
// - WAR · STRUCTURES: one spec row per strategic object, the contested
//   objects first under their own head. SHOW is the press. The icon is the
//   map glyph of the object (R1987). The row states every figure but the
//   gauge, which stands on the object card.
// - WAR · INTEL and the beams of WAR · SOVEREIGNTY: one fleet row per
//   contact or beam, the mark in the faction ink, SHOW as the press.
//
// The module is pure: every function is a function of the state and of
// the few arguments the overlay hands it.

import type { MarketListing, SiegeInfo } from '../../gen/proto/aquaphobia';
import { MarketItemKind } from '../../gen/proto/aquaphobia';
import { card as CS, overlay as S } from '../../gen/strings';
import { pad2 } from '../../locale/stamp';
import { type GameState, dur, num } from '../../state';
import { cellAt, factionName, mpxToPx } from '../../world';
import { cellPos } from '../chartpos';
import { buttonHtml } from '../cards/card';
import type { SceneLook } from '../../render/scene-look';
import { markSvg, shipMarkSvg, structureInk, structureMarkSvg } from '../cards/marks';
import { benefitText, cssColor, typeText } from '../format';
import { esc } from '../esc';
import { type ResIcon, resIcon, withResIcons } from '../resicon';
import { chip } from '../warcharts';
import { type PressState, buildRowHtml, pressHtml, rowIcon } from './buildrow';
import { loosePrint } from './draft';
import { sitePlace, sortStructures, subRow } from './markup';
import { AUCTION_PAGES } from './tables';
import { FL_SEP } from '../fleetlist';
import { fullInSec } from '../storewait';

/**
 * A section head of ECONOMY and WAR (R1892): the title in the cap step and
 * the dim ink, and an aside at the right, a count or a unit. Both are
 * escaped markup.
 */
export function secHead(title: string, aside?: string | number): string {
  return `<h3 class="sechd"><span>${title}</span>`
    + (aside !== undefined ? `<span class="aside">${aside}</span>` : '') + '</h3>';
}

// --- ECONOMY · PRODUCTION ---------------------------------------------------

/** The figures of one store row. */
export interface StoreFigures {
  key: ResIcon;
  name: string;
  have: number;
  /** The store cap, or 0 for a resource with no cap. */
  cap: number;
  /** The rate per game hour, or 0. */
  rate: number;
}

/** The five stores, in the order of the readout line. */
export function storeFigures(s: GameState): StoreFigures[] {
  const e = s.empire;
  const res = e?.res, caps = e?.caps, inc = e?.income;
  return [
    { key: 'alloy', name: S.deposit_names[0], have: res?.alloy ?? 0, cap: caps?.alloy ?? 0, rate: inc?.alloy ?? 0 },
    { key: 'biomass', name: S.deposit_names[1], have: res?.biomass ?? 0, cap: caps?.biomass ?? 0, rate: inc?.biomass ?? 0 },
    { key: 'volatiles', name: S.deposit_names[2], have: res?.volatiles ?? 0, cap: caps?.volatiles ?? 0,
      rate: inc?.volatiles ?? 0 },
    // Isotopes have no mine and no cap (numbers.md §2, R1425); credits
    // have no cap, and the idle mint is their one rate.
    { key: 'isotopes', name: S.deposit_names[3], have: res?.isotopes ?? 0, cap: 0, rate: 0 },
    { key: 'credits', name: S.res_credits, have: res?.credits ?? 0, cap: 0, rate: s.rules.idleCreditsPerHour },
  ];
}

/**
 * Line 2 of a store row: the rate per hour in the text ink and the time
 * to a full store, or "store full" in the bad ink, because a full store
 * stops the mine. Isotopes and credits state their source.
 */
export function storeLine2(f: StoreFigures): string {
  const rate = (r: number) => `<span class="rate">${S.rate_per_hour({ rate: num(r) })}</span>`;
  if (f.key === 'isotopes') return S.production_isotopes_src;
  if (f.key === 'credits') return `${rate(f.rate)} ${S.production_mint_src}`;
  if (f.cap > 0 && f.have >= f.cap) return `<span class="ink-bad">${S.production_full}</span>`;
  if (f.rate <= 0) return S.production_no_income;
  const left = fullInSec(f.have, f.cap, f.rate);
  const full = left !== undefined ? ` · ${S.production_full_in({ left: dur(left) })}` : '';
  return rate(f.rate) + full;
}

/** The figure column of a store row: the stock over its cap, and a store
 *  bar in the resource ink while the store has a cap. */
function storeFig(f: StoreFigures): string {
  const stock = `<span class="cost">${num(f.have)}${f.cap > 0 ? `<small> / ${num(f.cap)}</small>` : ''}</span>`;
  if (f.cap <= 0) return stock;
  const pct = Math.max(0, Math.min(100, f.have / f.cap * 100)).toFixed(1);
  return stock + `<i class="sbar r-${f.key}" style="--fill:${pct}%"><b></b></i>`;
}

/** The stores of ECONOMY · PRODUCTION: the head and one row per resource. */
export function storeRows(s: GameState): string {
  return secHead(S.production_head, S.production_head_aside) + storeFigures(s).map((f) => buildRowHtml({
    icon: `<span class="r-${f.key}">${resIcon(f.key)}</span>`,
    name: `<span class="name">${f.name}</span>`,
    line2: storeLine2(f),
    side: storeFig(f),
    press: '',
    cls: 'store',
    // Line 2 is a sentence ("while the build queue is idle"), and a
    // sentence never cuts (R2061).
    wrap: true,
  })).join('');
}

// --- ECONOMY · AUCTION ------------------------------------------------------

/** The press of a listing: DELIST on an own listing, BUY on every other,
 *  faint while the credits cannot pay (the server re-validates). */
function dealPress(s: GameState, l: MarketListing): string {
  const me = s.hello?.accountId ?? 0;
  if (l.seller === me) return pressHtml({ act: 'mcancel', id: l.id, word: S.btn_delist, state: 'ready' });
  const state: PressState = s.res.credits < l.price ? 'short' : 'ready';
  return pressHtml({ act: 'mbuy', id: l.id, word: S.btn_buy, state });
}

/** The price of a listing in the figure column, over the unit price of a lot. */
function priceFig(l: MarketListing, under = ''): string {
  return `<span class="cost">${resIcon('credits')}${num(l.price)}</span>`
    + (under ? `<span class="time">${under}</span>` : '');
}

/** One print listing: the print, its runs, its systems and its seller. */
function printListingRow(s: GameState, l: MarketListing): string {
  return buildRowHtml({
    icon: rowIcon('hull'),
    name: `<span class="name">${esc(l.print?.name ?? '?')}</span>`,
    kind: S.print_runs({ n: l.print?.runs ?? 0 }),
    line2: S.auction_print_sub({
      types: typeText(s, l.print?.weapType ?? 0, l.print?.shieldType ?? 0),
      inventor: esc(l.print?.inventor ?? '?'),
      seller: esc(l.sellerName),
    }),
    side: priceFig(l),
    press: dealPress(s, l),
  });
}

/** One isotope lot: the units, the seller, the price over the unit price. */
function lotListingRow(s: GameState, l: MarketListing): string {
  const per = l.qty > 0 ? (l.price / l.qty).toFixed(1) : '?';
  return buildRowHtml({
    icon: `<span class="r-isotopes">${resIcon('isotopes')}</span>`,
    name: `<span class="name">${S.auction_lot_name({ qty: num(l.qty) })}</span>`,
    kind: S.auction_lot,
    line2: esc(l.sellerName),
    side: priceFig(l, withResIcons(S.auction_unit({ price: per }))),
    press: dealPress(s, l),
  });
}

/**
 * ECONOMY · AUCTION (v12): two subpages. BROWSE groups the book by item
 * kind — prints, then isotope lots — with BUY on other people's rows and
 * DELIST on your own. ADD OFFER lists a print from the library or an
 * isotope lot (quantity + price); the dock-row SELL shortcut on
 * DRAFT · PRINTS keeps working beside it. Credits only, player to player.
 */
export function auctionTab(s: GameState, page: typeof AUCTION_PAGES[number]): string {
  const html = `<h3>${withResIcons(S.auction_head({
    count: s.market?.listings.length ?? 0, credits: num(s.res.credits),
  }))}</h3>` + subRow('aucpage', AUCTION_PAGES, page);
  return html + (page === 'BROWSE' ? auctionRows(s) : auctionOffer(s));
}

/** The BROWSE page of ECONOMY · AUCTION: prints, then isotope lots. */
export function auctionRows(s: GameState): string {
  const listings = s.market?.listings ?? [];
  // Item 0 is a print: every pre-v12 listing, kept meaningful (proto).
  const prints = listings.filter((l) => l.item !== MarketItemKind.MARKET_ITEM_KIND_ISOTOPES);
  const lots = listings.filter((l) => l.item === MarketItemKind.MARKET_ITEM_KIND_ISOTOPES);
  let html = secHead(S.auction_prints_head, prints.length);
  if (prints.length === 0) html += `<div class="empty">${S.auction_no_prints}</div>`;
  html += prints.map((l) => printListingRow(s, l)).join('');
  html += secHead(S.auction_lots_head, lots.length);
  if (lots.length === 0) html += `<div class="empty">${S.auction_no_lots}</div>`;
  html += lots.map((l) => lotListingRow(s, l)).join('');
  // What sells is a fact of the ECONOMY fold (R1895).
  return html;
}

/** The ADD OFFER page of ECONOMY · AUCTION: the loose prints and one
 *  isotope lot. */
function auctionOffer(s: GameState): string {
  const e = s.empire;
  if (!e) return '';
  const sellable = e.blueprints.filter((b) => loosePrint(s, b));
  // The slot rule (R692), the escrow and the lot size are facts of the
  // ECONOMY fold (R1895).
  let html = `<h3>${S.auction_sell_print_head}</h3>`;
  if (sellable.length === 0) {
    html += `<div class="empty">${S.auction_nothing_sellable}</div>`;
  }
  // One spec row per sellable print (round 091 card 09, R1892): the
  // price field stands in the figure column and LIST is the press.
  for (const bp of sellable) {
    html += buildRowHtml({
      icon: rowIcon('hull'),
      name: `<span class="name">${esc(bp.name)}</span>`,
      kind: S.print_runs({ n: bp.runs }),
      line2: typeText(s, bp.weapType, bp.shieldType),
      side: `<input id="price-${bp.id}" placeholder="¤" size="6" inputmode="numeric">`,
      press: { act: 'sell', id: bp.id, word: S.btn_list, state: 'ready' },
    });
  }
  html += `<h3>${withResIcons(S.auction_sell_iso_head({ held: num(s.res.isotopes) }))}</h3>`;
  html += `<div class="row"><span class="muted">${S.auction_lot}</span>`
    + `<span><input id="iso-qty" placeholder="${S.auction_units_placeholder}" size="6" inputmode="numeric">`
    + `<input id="iso-price" placeholder="${S.auction_total_placeholder}" size="7" inputmode="numeric">`
    + `<button class="primary" data-act="misolist">${S.btn_list}</button></span></div>`;
  return html;
}

// --- WAR · STRUCTURES -------------------------------------------------------

/**
 * The parts of the pledge line, or an empty list where no pledge stands
 * (R1892). The secrecy rule holds (R794): the own faction's count, your
 * own pledge, and a flag for every other faction, never its count.
 */
export function pledgeParts(g: SiegeInfo, short: boolean): string[] {
  const parts: string[] = [];
  if (g.youCommitted) {
    parts.push(`<span class="ink-good">${short ? S.structures_your_short : S.structures_your_pledge}</span>`);
  }
  if (g.ownCommits > 0) {
    parts.push(short ? S.structures_pledged_short({ own: g.ownCommits })
      : S.structures_own_pledges({ own: g.ownCommits }));
  }
  if (g.enemyPledged) {
    parts.push(`<span class="ink-amber">${short ? S.structures_enemy_short : S.structures_enemy_pledged}</span>`);
  }
  return parts;
}

/** The holder of an object, as chip + name, or "unclaimed". */
function objHolder(s: GameState, g: SiegeInfo): string {
  if (g.owner === 0) return S.word_unclaimed;
  const mine = g.owner === s.faction;
  return S.structures_held_by({ who: `${chip(g.owner)}${esc(factionName(g.owner))}${mine ? S.structures_yours : ''}` });
}

/** The attacker of an object, as chip + name, or ''. */
function objAttacker(g: SiegeInfo): string {
  return g.attacker ? S.structures_attacked({ who: `${chip(g.attacker)}${esc(factionName(g.attacker))}` }) : '';
}

/** The lead of line 2: the attacker when there is one, else the holder. */
function objLead(s: GameState, g: SiegeInfo): string {
  return objAttacker(g) || objHolder(s, g);
}

/**
 * The figure column of an object row: the window clock over the time to
 * it, or OPEN in the bad ink over the time left in the window. The clock
 * reads the live world clock, because a Sieges frame arrives on three
 * edges only (R733).
 */
export function windowFig(s: GameState, g: SiegeInfo): string {
  const inSec = s.windowInSec(g.windowHour);
  if (inSec < 0) {
    const left = Math.max(0, s.rules.objWindowSec + inSec);
    return `<span class="cost ink-bad">${S.structures_win_open}</span>`
      + `<span class="time">${S.structures_win_left({ left: dur(left) })}</span>`;
  }
  return `<span class="cost">${S.structures_win_hour({ hour: pad2(g.windowHour) })}</span>`
    + `<span class="time">${S.structures_win_in({ left: dur(inSec) })}</span>`;
}

/**
 * One object row: every figure but the gauge stands on it (R1892). The icon
 * is the glyph the map draws for the object, in the structure symbol set of
 * the reader's look (R1987). Three lines (R1993): the name beside the
 * window, the holder and the pledges beside the time to it, and the
 * benefit beside SHOW. SHOW opens the card of the object (R2517).
 */
function structureRow(s: GameState, g: SiegeInfo, look?: SceneLook): string {
  const name = sitePlace(g);
  const line2 = [objLead(s, g), ...pledgeParts(g, true)].filter((p) => p !== '').join(' · ');
  return buildRowHtml({
    icon: structureMarkSvg(g.kind, structureInk(g.owner, g.contested, g.attacker), look),
    name: `<span class="name">${esc(name)}</span>`,
    kind: esc(g.kindName),
    line2,
    line3: esc(benefitText(g.kind, g.benefit, g.benefit2)),
    side: windowFig(s, g),
    press: pressHtml({ act: 'focussite', id: g.siteId, n: g.cell, word: S.btn_show, state: 'ready' }),
    cls: 'obj',
  });
}

/**
 * The order of WAR · STRUCTURES (R1892): the contested objects first, then
 * every other object; each part keeps the order of `sortStructures`.
 */
export function structureOrder(list: readonly SiegeInfo[]): { contested: SiegeInfo[]; rest: SiegeInfo[] } {
  const all = sortStructures(list);
  return { contested: all.filter((g) => g.contested), rest: all.filter((g) => !g.contested) };
}

/** WAR · STRUCTURES (plan §4.3, R83, R95, R1892, R1987). */
export function structuresTab(s: GameState, look?: SceneLook): string {
  if (s.sieges.length === 0) return secHead(S.structures_head) + `<div class="empty">${S.structures_empty}</div>`;
  const { contested, rest } = structureOrder(s.sieges);
  let html = '';
  if (contested.length > 0) {
    html += secHead(S.structures_contested_head, contested.length)
      + contested.map((g) => structureRow(s, g, look)).join('');
  }
  if (rest.length > 0) {
    html += secHead(S.structures_head, rest.length) + rest.map((g) => structureRow(s, g, look)).join('');
  }
  return html;
}

// --- the fleet rows of WAR · INTEL and WAR · SOVEREIGNTY --------------------

/** The parts of one contact row in the fleet row look (R1816). */
interface ContactRow {
  mark: string;
  ink: string;
  cls: string;
  name: string;
  /** The parts of line 2, as markup. */
  line2: string[];
  /** A fill of line 2, 0 to 1, with its label: the hull or the progress. */
  bar?: { label?: string; frac: number };
  /** The cell SHOW centres the map on, and the title of the press. */
  cell: number;
  title: string;
}

/** One contact row as markup: the fleet row with SHOW as its press, no
 *  group digit and no ⋯. */
function contactRowHtml(r: ContactRow): string {
  const pct = Math.max(0, Math.min(100, (r.bar?.frac ?? 0) * 100));
  const bar = r.bar
    ? [`<span class="fl-v hull">${esc(r.bar.label ?? '')}<i class="fl-vb"><b></b></i>${Math.round(pct)}%</span>`] : [];
  return `<div class="fl-row sea contact" style="--fl-ink:${r.ink};--fl-hull:${pct.toFixed(1)}%">`
    + `<span class="fl-main"><span class="fl-mk">${r.mark}</span>`
    + `<span class="fl-l1"><b class="fl-cls">${esc(r.cls)}</b><span class="fl-nm">${r.name}</span></span>`
    + `<span class="fl-l2">${[...bar, ...r.line2].join(FL_SEP)}</span></span>`
    + '<span class="fl-grp"></span>'
    + `<span class="fl-press">${buttonHtml({
      act: 'focuscell', n: r.cell, label: S.btn_show, face: 'quiet', title: r.title,
    }, { keycaps: false })}</span></div>`;
}

/**
 * WAR · INTEL (plan §4.4, R83, R1892): the scan shelf, one fleet row per
 * contact. The mark of its class in the faction ink, the owner the scan
 * named (R1436) or the faction, and on line 2 the hull, the place, the age,
 * the systems and the scanner. SHOW centres the map where it was pinged.
 */
export function intelTab(s: GameState): string {
  const list = s.war?.intel ?? [];
  if (list.length === 0) {
    // The shelf rules are facts of the WAR fold (R1895).
    return secHead(S.intel_head) + `<div class="empty">${S.intel_empty}</div>`;
  }
  // Scan ages are tactical seconds (R91): the shelf ages with how far a
  // contact could have sailed. The tab states them in world time (R591).
  const sinceFetch = Math.max(0, (Date.now() - s.warAtMs) / 1000) * s.tacticalScale;
  const rows = list.filter((e) => e.fleet).map((e) => {
    const f = e.fleet!;
    const cell = cellAt(mpxToPx(f.xMpx), mpxToPx(f.yMpx));
    return contactRowHtml({
      mark: shipMarkSvg(f.cls), ink: cssColor(f.faction), cls: s.ship(f.cls)?.name ?? '?',
      name: esc(f.ownerName || factionName(f.faction)),
      bar: { label: CS.v_hull, frac: f.hullPct / 100 },
      line2: [S.intel_line({
        pos: cellPos(cell), age: dur(s.mapToWorldSec(e.ageSec + sinceFetch)),
        types: esc(typeText(s, f.weapType, f.shieldType)), scanner: esc(e.scanner),
      })],
      cell, title: S.intel_centre_title,
    });
  });
  return secHead(S.intel_head, rows.length) + rows.join('');
}

/** One sovereignty beam in view, one entry per working source (R116). */
interface BeamRow {
  sourceId: number; beacon: boolean; faction: number;
  targetCell: number; progress: number; against: number;
}

/**
 * One beam of WAR · SOVEREIGNTY as a fleet row (R1892): the beacon or the
 * hold mark in the faction ink, the hull and its commander where the
 * client can name them (R1570), else the faction; line 2 the progress and
 * what it takes; SHOW centres the map on the target cell.
 */
export function beamRowHtml(b: BeamRow, named: { name: string; ownerName: string } | null): string {
  const name = named && named.name && named.ownerName
    ? S.beam_fleet({ fleet: esc(named.name), owner: esc(named.ownerName) })
    : esc(factionName(b.faction));
  const from = b.against === 0 ? S.word_open_water : esc(factionName(b.against));
  const ink = cssColor(b.faction);
  return contactRowHtml({
    mark: markSvg(b.beacon ? 'fleet' : 'hold', ink), ink,
    cls: b.beacon ? S.word_beacon : S.word_hold, name,
    bar: { frac: b.progress / 100 },
    line2: [S.beam_taking({ from, pos: cellPos(b.targetCell) })],
    cell: b.targetCell, title: S.centre_map_title,
  });
}
